import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, 
  FiPlus, 
  FiMoreHorizontal, 
  FiCalendar,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiUsers,
  FiUser
} from 'react-icons/fi';
import { HiChevronDoubleLeft, HiChevronLeft, HiChevronRight, HiChevronDoubleRight } from 'react-icons/hi';
import { useUser } from '@clerk/clerk-react';
import { fetchProjects } from '../services/api';
import { useCache } from '../contexts/CacheContext';
import OrganizationSelector from '../components/OrganizationSelector';
// Remove import { config, fetchProjects } from '../services/api';
// Remove all useEffect, fetch, and data processing logic that uses DB inputs
// Only display the projects layout with placeholders or empty states

const Projects = () => {
  // Get user from Clerk
  const { user, isLoaded } = useUser();
  
  // Get cache functions
  const { getProjects } = useCache();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'personal', 'organization'
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  

  
  // Remove import { config, fetchProjects } from '../services/api';
  // Remove all useEffect, fetch, and data processing logic that uses DB inputs
  // Only display the projects layout with placeholders or empty states
  
  const formatDateFromIso = (isoDate) => {
    if (!isoDate) return 'Unknown date';
    
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };
  
  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    
    return tokens.toString();
  };

  const formatCost = (costs) => {
    if (!costs || typeof costs !== 'object') return '$0.00';
    
    const totalCost = Object.values(costs).reduce((sum, cost) => sum + (cost || 0), 0);
    return `$${totalCost.toFixed(4)}`;
  };
  
  const handleCheckboxChange = (id) => {
    if (selectedProjects.includes(id)) {
      setSelectedProjects(selectedProjects.filter(projectId => projectId !== id));
    } else {
      setSelectedProjects([...selectedProjects, id]);
    }
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentFilteredProjects = getFilteredProjects();
      setSelectedProjects(currentFilteredProjects.map(project => project.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Helper function to get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Helper function to get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Check if a date has projects
  const hasProjectsOnDate = (date) => {
    const currentFilteredProjects = getFilteredProjects();
    return currentFilteredProjects.some(project => 
      project.dateObj.getDate() === date.getDate() &&
      project.dateObj.getMonth() === date.getMonth() &&
      project.dateObj.getFullYear() === date.getFullYear()
    );
  };

  // Format date as string (e.g., "May 1, 2025")
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const getFilteredProjects = () => {
    let filtered = projects;
    
    // Ensure we only show organization projects
    filtered = filtered.filter(project => 
      project.project_type === 'organization' || 
      (project.organization_name && project.organization_name !== null)
    );
    
    // Filter by selected organization
    if (selectedOrganization) {
      // Get the organization name from the selected organization
      const selectedOrgName = selectedOrganization.name || selectedOrganization;
      
      // Further filter by specific organization
      filtered = filtered.filter(project => 
        project.organization_id === selectedOrganization.id || 
        project.organization_name === selectedOrgName
      );
    }

    // Filter by search query if it exists
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(project => 
        project.name.toLowerCase().includes(query) || 
        (project.models && project.models.some(model => model.toLowerCase().includes(query))) ||
        (project.organization_name && project.organization_name.toLowerCase().includes(query))
      );
    }

    // Only filter by date if user selects a date
    if (selectedDate) {
      console.log('Filtering by date:', selectedDate);
      console.log('Available projects:', filtered.map(p => ({ name: p.name, dateObj: p.dateObj, date: p.date })));
      
      filtered = filtered.filter(project => {
        if (!project.dateObj) return false;
        const projectDate = project.dateObj;
        const matches = (
          projectDate.getDate() === selectedDate.getDate() &&
          projectDate.getMonth() === selectedDate.getMonth() &&
          projectDate.getFullYear() === selectedDate.getFullYear()
        );
        console.log(`Project ${project.name}: ${projectDate.toDateString()} vs ${selectedDate.toDateString()} = ${matches}`);
        return matches;
      });
      
      console.log('Filtered projects:', filtered.map(p => p.name));
    }

    return filtered;
  };

  // Generate calendar UI
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const monthNames = ["January", "February", "March", "April", "May", "June", 
                        "July", "August", "September", "October", "November", "December"];
    
    // Create calendar grid
    const days = [];
    const blanks = [];
    
    // Add blank spaces for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      blanks.push(<div key={`blank-${i}`} className="p-2"></div>);
    }
    
    // Add days of month
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const hasProjects = hasProjectsOnDate(date);
      const isSelected = selectedDate && 
                         selectedDate.getDate() === d && 
                         selectedDate.getMonth() === month &&
                         selectedDate.getFullYear() === year;
      
      days.push(
        <div 
          key={`day-${d}`} 
          onClick={() => handleDateClick(date)}
          className={`
            p-2 text-center cursor-pointer rounded-full w-8 h-8 mx-auto flex items-center justify-center
            hover:bg-blue-100
            ${hasProjects ? 'font-semibold' : 'text-gray-500'}
            ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
          `}
        >
          {d}
        </div>
      );
    }
    
    // Combine blanks and days
    const totalSlots = [...blanks, ...days];
    const rows = [];
    let cells = [];
    
    // Create rows with 7 cells each (for each day of week)
    totalSlots.forEach((slot, i) => {
      if (i % 7 !== 0) {
        cells.push(slot);
      } else {
        rows.push(cells);
        cells = [];
        cells.push(slot);
      }
      if (i === totalSlots.length - 1) {
        rows.push(cells);
      }
    });
    
    return (
      <div className="p-4">
        {/* Month navigation */}
        <div className="flex justify-between items-center mb-4">
          <button onClick={() => navigateMonth(-1)} className="text-gray-500 hover:text-gray-700">
            <FiChevronLeft />
          </button>
          <h3 className="font-medium">{monthNames[month]} {year}</h3>
          <button onClick={() => navigateMonth(1)} className="text-gray-500 hover:text-gray-700">
            <FiChevronRight />
          </button>
        </div>
        
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="text-center text-xs text-gray-500 font-medium">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div>
          {rows.map((row, index) => (
            <div key={index} className="grid grid-cols-7 mb-2">
              {row}
            </div>
          ))}
        </div>
        
        {/* Selected date display and clear button */}
        {selectedDate && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Selected: {formatDate(selectedDate)}
            </span>
            <button 
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={clearDateFilter}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  };

  const modelBadgeColor = (model) => {
    const modelLower = model.toLowerCase();
    switch (modelLower) {
      case 'gpt-4':
      case 'gpt-4o':
        return 'bg-blue-100 text-blue-600';
      case 'gpt-4o-large':
        return 'bg-indigo-100 text-indigo-600';
      case 'gpt-4o-mini':
        return 'bg-cyan-100 text-cyan-600';
      case 'gpt-3.5-turbo':
        return 'bg-green-100 text-green-600';
      case 'claude':
      case 'claude-3':
        return 'bg-purple-100 text-purple-600';
      case 'llama-3':
        return 'bg-amber-100 text-amber-600';
      case 'mistral-7b':
        return 'bg-red-100 text-red-600';
      case 'custom llm-x':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  useEffect(() => {
    // Fetch projects with user authentication
    const loadRealProjects = async () => {
      setLoading(true);
      try {
        // Only load data if user is authenticated
        if (!user) {
          setProjects([]);
          return;
        }

        // Pass user ID to API call for authentication with caching
        const response = await getProjects(user.id, () => fetchProjects(null, user.id, false));
      if (response && response.projects) {
        // Transform API data to match our project structure
          const formattedProjects = response.projects.map((project, index) => {
            // Extract models from runs if available
            const models = [];
            if (project.runs && project.runs.length > 0) {
              project.runs.forEach(run => {
                if (run.llm_calls && run.llm_calls.length > 0) {
                  run.llm_calls.forEach(call => {
                    if (call.model && !models.includes(call.model)) {
                      models.push(call.model);
                    }
                  });
                }
              });
            }

            // Determine project type based on organization_id or project_type
            let projectType = project.project_type;
            if (!projectType) {
              // Fallback: if organization_id exists, it's an organization project
              projectType = project.organization_id ? 'organization' : 'personal';
            }



            return {
              id: project.project_id || project.id || project._id || index + 1,
              name: project.project || project.name || `Project ${index + 1}`,
              date: formatDateFromIso(project.CreatedAt || project.created_at),
              dateObj: project.CreatedAt || project.created_at ? new Date(project.CreatedAt || project.created_at) : new Date(),
              models: models,
              tokenUsage: formatTokens(project.token_usage || project.tokens_used || 0),
            latency: `${Math.round((project.avg_latency || 0))} ms`,
          clients: 1, // Placeholder, update if you have client info
              status: 'active', // Default status
              cost: formatCost(project.costs || project.total_cost || {}),
              runCount: project.runs ? project.runs.length : 0,
              project_type: projectType,
              organization_id: project.organization_id,
              organization_name: project.organization_name,
              created_by: project.created_by,
              visibility: project.visibility,
              access_level: project.access_level,
              successful_runs: project.successful_runs || 0,
              success_rate: project.success_rate || 0
            };
          });
        setProjects(formattedProjects);
      } else {
          setError('No projects found in database.');
      }
    } catch (err) {
        // Don't show error, just set empty data
        setProjects([]);
        console.error('Projects loading error:', err);
      } finally {
      setLoading(false);
    }
  };
    loadRealProjects();
  }, [user]);

  // Remove handleMongoConnect

  const filteredProjects = getFilteredProjects();

  return (
    <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-blue-800">Projects</h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600">Organization:</span>
                <OrganizationSelector 
                  onOrganizationChange={setSelectedOrganization}
                  selectedOrgId={selectedOrganization}
                />
              </div>
            </div>
          </div>

          {/* Project Type Tabs */}
          <div className="flex rounded-md overflow-hidden border border-gray-200 mb-6 w-fit">
            <button 
              onClick={() => setActiveTab('all')}
              className={`px-6 py-2 flex items-center gap-2 ${activeTab === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <FiUsers className="w-4 h-4" />
              Organization Projects ({filteredProjects.length})
            </button>
          </div>

          {/* Project Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {selectedOrganization ? 'Filtered Projects' : 'Total Organization Projects'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{filteredProjects.length}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiUsers className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Projects</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredProjects.filter(p => p.status === 'active').length}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiUsers className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row justify-end items-center mb-6 gap-4">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="pl-10 pr-4 py-2 rounded-md border border-gray-300 w-full sm:w-72"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchQuery && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button 
                  className={`flex items-center px-4 py-2 rounded-md ${selectedDate ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-600'}`}
                  onClick={toggleCalendar}
                >
                  <FiCalendar className="mr-2" />
                  {selectedDate ? formatDate(selectedDate) : 'Select Date'}
                </button>
                
                {showCalendar && (
                  <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10 w-80">
                    <div className="flex justify-between items-center p-4 border-b">
                      <h3 className="font-medium">Calendar</h3>
                      <button 
                        className="text-gray-400 hover:text-gray-600"
                        onClick={toggleCalendar}
                      >
                        <FiX />
                      </button>
                    </div>
                    {renderCalendar()}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Loading and Error States */}
          {loading && (
            <div className="bg-white rounded-md shadow-sm p-8 text-center">
              <p className="text-gray-600">Loading projects...</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 rounded-md shadow-sm p-4 mb-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}
          {/* Project Table */}
          {!loading && !error && (
            <div className="bg-white rounded-md shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-200 text-sm text-gray-500">
                    <th className="p-4 text-left">
                      <input 
                        type="checkbox" 
                        className="rounded"
                        onChange={handleSelectAll}
                        checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0}
                      />
                    </th>
                    <th className="p-4 text-left font-medium flex items-center">
                      Project Name
                      <button className="ml-1">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
                        </svg>
                      </button>
                    </th>
                    <th className="p-4 text-left font-medium">
                      <div className="flex items-center">
                        Organization
                        <button className="ml-1">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    </th>
                    <th className="p-4 text-left font-medium">
                      <div className="flex items-center">
                        Created Date
                        <button className="ml-1">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    </th>
                    <th className="p-4 text-left font-medium">
                      <div className="flex items-center">
                        Models
                        <button className="ml-1">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 10l5 5 5-5H7z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    </th>
                    <th className="p-4 text-left font-medium">Token Usage</th>
                    <th className="p-4 text-left font-medium">Cost</th>
                    <th className="p-4 text-left font-medium">Runs</th>
                    <th className="p-4 text-left font-medium">Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="p-8 text-center text-gray-500">
                        No projects found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredProjects.map(project => (
                      <tr key={project.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-4">
                          <input 
                            type="checkbox" 
                            className="rounded"
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleCheckboxChange(project.id)}
                          />
                        </td>
                        <td className="p-4 font-medium">
                          <Link to={`/projects/${project.id}`} className="text-blue-600 hover:underline">
                            {project.name}
                          </Link>
                        </td>
                        <td className="p-4 text-gray-500">
                          {project.organization_name ? (
                            <span className="flex items-center gap-1">
                              <FiUsers className="w-3 h-3" />
                              {project.organization_name}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-4 text-gray-500">{project.date}</td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {project.models.length > 0 ? (
                              project.models.map((model, idx) => (
                              <span key={idx} className={`${modelBadgeColor(model)} text-xs px-2 py-1 rounded`}>
                                {model}
                              </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">No models</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-gray-500">{project.tokenUsage}</td>
                        <td className="p-4 text-gray-500">{project.cost}</td>
                        <td className="p-4 text-gray-500">{project.runCount}</td>
                        <td className="p-4">
                          <span className={`inline-block px-2 py-1 rounded-md text-xs ${
                            project.success_rate >= 0.9 
                              ? 'bg-green-100 text-green-600' 
                              : project.success_rate >= 0.7
                              ? 'bg-yellow-100 text-yellow-600'
                              : 'bg-red-100 text-red-600'
                          }`}>
                            {(project.success_rate * 100).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>

              {/* Pagination */}
              <div className="border-t border-gray-200 p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-500">
                  Showing {filteredProjects.length} of {projects.length} organization projects
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <HiChevronDoubleLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <HiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center bg-blue-600 text-white min-h-[44px] min-w-[44px]">
                    1
                  </button>
                  <button className="border border-gray-300 rounded-md w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px]">
                    2
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <HiChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <HiChevronDoubleRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </div>
      )}
    </div>
  );
};

export default Projects; 